import React, { useEffect, useState, useRef, useCallback } from "react";
import clsx from "clsx";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { toast } from "react-toastify";
import {
  formatDate
} from "../../../utils/formatter"
import useOutsideAlerter from "../../../utils/clickOutside";

import styles from "./inadimplentes.module.css";
import { useInstitution } from "../../../context/InstitutionContext";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import ZeroData from "../../../components/zeroData/zeroData";
import InadimplenciaIcon from "../../../assets/icons/inadimplencia-icon.svg";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import MagnifyingGlassIcon from "../../../assets/icons/magnifying-glass-blue.svg";
import loadingGif from "../../../assets/loading.gif";

import Button from "../../../components/button/button";
import ClassPredictionsTable from "../../../components/inadimplentes/classPredictionsTable/classPredictionsTable";
import HighRiskAnalysisTable from "../../../components/inadimplentes/highRiskAnalysisTable/highRiskAnalysisTable";
import TopCourseRiskCard from "../../../components/inadimplentes/topCourseRiskCard/topCourseRiskCard";
import { predictiveFinanceInstitution } from "../../../services/prediction";
import { getDateRange, saveDateRange } from "../../../utils/localStorage";


const Inadimplentes = () => {
  const { institution } = useInstitution();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inadimplenciaData, setInadimplenciaData] = useState(null);
  const [error, setError] = useState(null);
  const [suggestionRange, setSuggestionRange] = useState("");
  const [dateRangeString, setDateRangeString] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });

  const calendarRef = useRef(null);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    );
  }, [dateRange]);

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  const fetchInadimplenciaData = useCallback(
    async ({ startDate, endDate }) => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await predictiveFinanceInstitution({
          institutionId: institution.id,
          startDate,
          endDate
        });

        console.log("Resposta da API PredictiveFinanceInstitution:", response);
        
        setInadimplenciaData(response);
        setIsCalendarOpen(false);
      } catch (error) {
        console.error("Erro ao buscar dados de inadimplência:", error);
        setError("Erro ao buscar dados de inadimplência");
        toast.error("Erro ao buscar dados de inadimplência");
        setInadimplenciaData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [institution.id]
  );

  useEffect(() => {
    if (!institution.id) return;

    const timeoutId = setTimeout(() => {
      fetchInadimplenciaData({
        startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
        endDate: formatDate(dateRange?.to).replaceAll("/", "-")
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [institution.id, dateRange, fetchInadimplenciaData]);

  const selectManualRange = () => {
    setSuggestionRange("");
    // Não faz chamada aqui - o useEffect vai detectar a mudança no dateRange e chamar automaticamente
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);

    if (!institution.id) return;
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = {
          from: new Date(),
          to: new Date()
        };
        break;
      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;
      case "last_30":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 30)),
          to: new Date()
        };
        break;
      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;
      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;
      case "last_year":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
          to: new Date()
        };
        break;
      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;
      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;
      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);
    // Não faz chamada aqui - o useEffect vai detectar a mudança no dateRange e chamar automaticamente
  };

  const handleClearSelection = () => {
    setSuggestionRange("");
    setDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  return (
    <div className={styles.dashboardWrapper}>
      <HeaderTitle title="Inadimplência" icon={InadimplenciaIcon}>
        <div className={styles.headerTitleActions}>
          <div className={styles.headerTitleActionsCalendar}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calenderContent}>
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      onClick={() => !isLoading && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !isLoading && handleClearSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>
                    <div
                      onClick={() => !isLoading && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{ 
                        opacity: isLoading ? 0.8 : 1, 
                        cursor: isLoading ? 'not-allowed' : 'pointer' 
                      }}
                    >
                      {isLoading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button
            icon={BarsFilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            disabled={true}
          />
        </div>
      </HeaderTitle>

      <div className={styles.dashboardBody}>
        <div className={styles.financeContent}>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px"
            }}
          >
            <img
              src={loadingGif}
              alt="Carregando..."
              style={{ width: "60px", height: "60px" }}
            />
          </div>
        ) : error ? (
          <ZeroData
            icon={InadimplenciaIcon}
            title="Erro ao carregar dados"
            description={error}
          />
        ) : !inadimplenciaData || (Array.isArray(inadimplenciaData) && inadimplenciaData.length === 0) ? (
          <ZeroData
            icon={InadimplenciaIcon}
            title="Nenhum dado de inadimplência encontrado"
            description="Use os filtros de data para buscar indicadores de inadimplência"
          />
        ) : (
          <>
            {/* 1. Curso com maior risco de inadimplência + fatores que geram esse risco */}
            <div className={styles.grid_1}>
              <TopCourseRiskCard 
                classPredictions={inadimplenciaData.data?.classPredictionsSummary} 
                topFactors={inadimplenciaData.data?.topFactors}
              />
            </div>

            {/* 2. Tabela com inadimplência prevista para cada curso */}
            <div className={styles.grid_1}>
              <ClassPredictionsTable classPredictions={inadimplenciaData.data?.classPredictionsSummary} />
            </div>

            {/* 3. Cursos com maior risco de se tornar inadimplentes */}
            <div className={styles.grid_1}>
              <HighRiskAnalysisTable highRiskAnalysis={inadimplenciaData.data?.highRiskAnalysis} />
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Inadimplentes;
